import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Card, Button, Row, Col, Form, Table, Container, Modal } from 'react-bootstrap'
import { ContactsService, OpportunitiesService, InstituteService } from '../../../service/contacts-service'
import { PrivacyService } from '../../../service/privacy-service'
import { InviaAffidaService } from '../../../service/invia-affida-service'
import { useParams, Link, useLocation, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { successToast, warningToast } from '../../../constants/Toasts-Components'
import { formatCurrency } from '../../../constants/constant-func'
import ServicesRequest from '../../../service/ServiceRequest'
import ReactLoading from "react-loading"
import R from 'raphael'

const parseISODate = (value) => {
  if (!value) return null
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

// The installment due on the reference date is still outstanding.
const monthsElapsed = (startDate, endDate) => {
  const start = parseISODate(startDate)
  const end = parseISODate(endDate)
  if (!start || !end) return null
  let months = (end.year - start.year) * 12 + (end.month - start.month)
  if (end.day <= start.day) months -= 1
  return months
}

const todayISO = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

// Residuo = rate still left * rata. Rate residue = duration - months since start_date.
const residuoRateResidue = (trat, referenceDate = todayISO()) => {
  const amount = parseFloat(trat.amount)
  const duration = parseInt(trat.duration, 10)
  const elapsed = monthsElapsed(trat.start_date, referenceDate)
  if (amount > 0 && duration > 0 && elapsed != null) {
    const rateResidue = Math.min(duration, Math.max(0, duration - Math.max(0, elapsed)))
    return rateResidue * amount
  }
  const stored = parseFloat(trat.residuo)
  return isNaN(stored) ? 0 : stored
}

const CurrencyInput = ({ value, onChange, name, step, readOnly, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);

  // Determine decimals based on step
  let decimals = 2;
  if (step == 1) decimals = 0;
  else if (step == 0.1) decimals = 1;

  const handleFocus = (e) => {
    if (!readOnly) setIsFocused(true);
    if (props.onFocus) props.onFocus(e);
  };
  
  const handleBlur = (e) => {
    setIsFocused(false);
    if (props.onBlur) props.onBlur(e);
  };

  const showRaw = isFocused && !readOnly;

  const displayValue = showRaw 
    ? value 
    : formatCurrency(value, decimals);

  return (
    <Form.Control
      type={showRaw ? "number" : "text"}
      name={name}
      value={displayValue || ''}
      onChange={onChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      step={step}
      readOnly={readOnly}
      {...props}
    />
  );
};

const emptyContact = {
  name: '', surname: '', email: '', telefone: '', codice_fiscale: '', luogo_di_nascita: '', preferenza_orario: '', assigned_user: '', assigned_agency: '',
  codice_fiscale_datore: '',
  cittadinanza: '',
  telefone: '',
  residente_in_it: '',
  sesso: '',
  residenza: '',
  categoria_di_lavoro: '',
  data_di_nascita: '',
  data_di_assunzione: '',
  is_part_time: false,
  part_time_percentage: 0,
  retribuzione: 0,
  chiave_pensione: 0,
  mensilita: 0,
  tfr: 0,
  tfr_data_riferimento: '',
  is_tempo_indeterminato: false,
  malattia: 0,
  cig_oltre_40_ore: 0,
  quinto_cedibile: '',
  has_maturing_trattenuta: false
}

const opportunityStatesMap = {
  "1": "Nuova opportunità",
  "2A": "Richiesta documenti cliente",
  "2B": "Richiesta Quota B.O. Istituto",
  "3A": "Documenti Rientrati",
  "3B": "Quota Rientrata",
  "4A": "Preventivo accettato",
  "4B": "Preventivo non Accettato",
  "4C": "Cliente Ci Pensa",
  "5": "Controllo Qualità B.O. Interno",
  "6A": "In attesa di integrazione documenti",
  "6B": "Assegnato B.O. Istituto",
  "7": "Documenti Rientrati",
  "8A": "In attesa di CDS",
  "8B": "In attesa di cedolino pensione",
  "9": "Caricata Banca",
  "10": "Contratto firmato",
  "11": "Contratto Notificato",
  "12": "Contratto liquidato",
  "13": "Annullato",
  "14": "Mai Fattibile",
  "15": "Maturazione TFR"
}

const ContactAddOrView = () => {
  const params = useParams()
  const isNew = !params.contact_id
  const service = new ContactsService()
  const location = useLocation()
  const history = useHistory()
  const { config } = useSelector((state) => state.auth)
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState(emptyContact)
  const [contactId, setContactId] = useState(null)
  const [newComment, setNewComment] = useState("")
  const [files, setFiles] = useState([])
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadQueue, setUploadQueue] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [showOpportunityModal, setShowOpportunityModal] = useState(false)
  const [showTrattenutaModal, setShowTrattenutaModal] = useState(false)
  const [opportunities, setOpportunities] = useState([])
  const [trattenute, setTrattenute] = useState([])
  const [products, setProducts] = useState([])
  const [institutes, setInstitutes] = useState([])
  const [campaigns, setCampaigns] = useState([])
  const [users, setUsers] = useState([])
  const [agencies, setAgencies] = useState([])
  const [assignmentType, setAssignmentType] = useState('') // 'user' or 'agency'
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedAgencyId, setSelectedAgencyId] = useState('')
  const [showReassignModal, setShowReassignModal] = useState(false)
  const [reassignType, setReassignType] = useState('')
  const [reassignSelectedUserId, setReassignSelectedUserId] = useState('')
  const [reassignSelectedAgencyId, setReassignSelectedAgencyId] = useState('')
  const [showStateChangeModal, setShowStateChangeModal] = useState(false)
  
  const [showPrivacyModal, setShowPrivacyModal] = useState(false)
  const [privacyDate, setPrivacyDate] = useState('')
  const [privacyTime, setPrivacyTime] = useState('')
  const [privacyNote, setPrivacyNote] = useState('')
  
  const [selectedOpportunity, setSelectedOpportunity] = useState(null)
  const [activeOpportunity, setActiveOpportunity] = useState(null)
  const [selectedNewState, setSelectedNewState] = useState(null)
  const [viewingOpportunity, setViewingOpportunity] = useState(null)
  const [showCreateOpportunityWithoutQuoteModal, setShowCreateOpportunityWithoutQuoteModal] = useState(false)
  // Contact status quick-change UI
  const [showContactStatusModal, setShowContactStatusModal] = useState(false)
  const [pendingContactStatus, setPendingContactStatus] = useState('')
  const [pendingDataPerRichiamare, setPendingDataPerRichiamare] = useState('')
  const [savingContactStatus, setSavingContactStatus] = useState(false)
  
  // Go Back to Previous State Logic
  const [showGoBackModal, setShowGoBackModal] = useState(false);
  const [opportunityToGoBack, setOpportunityToGoBack] = useState(null);
  const [selectedPreviousState, setSelectedPreviousState] = useState("");

  // Opportunity History Logic
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyOpportunity, setHistoryOpportunity] = useState(null);
  const [sendingPrivacy, setSendingPrivacy] = useState(false);
  const [sendingAffida, setSendingAffida] = useState(false);
  const affidaSubmissionLockedRef = useRef(false);
  const servicesHidden = config?.is_agent && config?.services_allowed === false;

  const handleShowHistory = async (opportunity) => {
    setHistoryOpportunity(opportunity);
    setShowHistoryModal(true);
    setLoadingHistory(true);
    try {
      const oppService = new OpportunitiesService();
      const data = await oppService.history(opportunity.id);
      setHistoryData(data);
    } catch (error) {
      console.error("Error fetching history:", error);
      warningToast("Errore durante il caricamento della cronologia");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRevertState = async (stateCode) => {
    if (!historyOpportunity) return;
    
    if (!window.confirm(`Sei sicuro di voler tornare allo stato selezionato?`)) {
      return;
    }

    try {
      const oppService = new OpportunitiesService();
      await oppService.save(historyOpportunity.id, { state: stateCode });
      successToast("Stato ripristinato con successo");
      setShowHistoryModal(false);
      load(false); // Refresh main data
    } catch (error) {
      console.error("Error reverting state:", error);
      warningToast("Errore durante il ripristino dello stato");
    }
  };

  const handleGoBack = (opportunity) => {
      setOpportunityToGoBack(opportunity);
      setSelectedPreviousState(""); // Reset selection
      setShowGoBackModal(true);
  };

  const handleConfirmGoBack = async () => {
      if (!opportunityToGoBack || !selectedPreviousState) return;

      try {
          const oppService = new OpportunitiesService();
          await oppService.save(opportunityToGoBack.id, { state: selectedPreviousState });
          setShowGoBackModal(false);
          setOpportunityToGoBack(null);
          setSelectedPreviousState("");
          successToast("Stato opportunità aggiornato con successo");
          load(false); // Refresh data
      } catch (error) {
          console.error("Error going back to previous state:", error);
          warningToast("Errore durante l'aggiornamento dello stato");
      }
  };
  
  // Check if we came from opportunities page or dashboard
  const fromOpportunities = location.state?.fromOpportunities || false
  const fromDashboard = location.state?.fromDashboard || false

  console.log("User:", config)

  function onChange(e){
    const field = e.target.name
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value

    if (field === 'quinto_cedibile' || field === 'part_time_percentage' || field === 'tfr' || field === 'mensilita') {
        if (value.includes('.') && value.split('.')[1].length > 1) {
            return
        }
    }
    
    if (field === 'retribuzione' || field === 'chiave_pensione') {
        if (value.includes('.')) {
            return
        }
    }
    
    setInputs({ ...inputs, [e.target.name]: value })
  }

  // Function to check if user has edit access to an opportunity
  function hasEditAccess(opportunity) {
    if (!config) return false
    if (config.is_agency_admin) return false
    if (!opportunity) {
      if (config.is_admin || config.is_agency_boss || config.is_agency_admin || config.is_agent) {
        return true
      }
      else {
        return false
      }
    }
    return opportunity.state.edit_access.includes(config.role_string)
  }

  function hasReadAccess(opportunity) {
    if (!config) return false
    if (config.is_agency_admin) return false
    if (!opportunity) {
      if (config.is_admin || config.is_agency_boss || config.is_agency_admin || config.is_agent) {
        return true
      }
      else {
        return false
      }
    }
    return opportunity.state.read_access.includes(config.role_string)
  }

  // Function to get phone number from form field
  function getPhoneNumber() {
    return inputs.telefone || ''
  }

  // Function to generate contact title
  function getContactTitle() {
    if (isNew) return 'Aggiungi Contatto'
    
    const phone = getPhoneNumber()
    const phoneDisplay = phone ? ` - ${phone}` : ''
    const emailDisplay = inputs.email ? ` - ${inputs.email}` : ''
    
    return `${contactId} - ${inputs.name} ${inputs.surname}${emailDisplay}${phoneDisplay}`
  }

  async function loadProducts(){
    const productService = new ServicesRequest("product")
    const response = await productService.getProductListComplete()
    if (!response.response) {
      setProducts(response.data || [])
    }
  }

  async function loadInstitutes(){
    const instituteService = new InstituteService()
    const response = await instituteService.list()
    if (response && response.data) {
      setInstitutes(response.data)
    }
  }

  async function loadCampaigns(){
    const campaignService = new ServicesRequest("campaign")
    const response = await campaignService.getProductListComplete()
    if (!response.response) {
      setCampaigns(response.data || [])
    }
  }

  async function loadUsers(){
    const userService = new ServicesRequest("user")
    const response = await userService.getProductListComplete()
    if (!response.response) {
      // Backend already filters users based on user role and permissions
      // No need for additional frontend filtering
      setUsers(response.data || [])
    }
  }

  async function loadAgencies(){
    const agencyService = new ServicesRequest("agency")
    const response = await agencyService.getProductListComplete()
    if (!response.response) {
      setAgencies(response.data || [])
    }
  }

  async function handleSendPrivacy() {
    if (!contactId) return
    if (!privacyDate || !privacyTime) {
      warningToast('Inserisci data e ora appuntamento')
      return;
    }
    setSendingPrivacy(true)
    try {
      const privacyService = new PrivacyService()
      await privacyService.send(contactId, privacyDate, privacyTime, privacyNote)
      successToast('Privacy inviata con successo!')
      setShowPrivacyModal(false)
      setPrivacyDate('')
      setPrivacyTime('')
      setPrivacyNote('')
    } catch (error) {
      console.error('Error sending privacy:', error)
      warningToast(error.response?.data?.error || 'Errore durante l\'invio della privacy')
    } finally {
      setSendingPrivacy(false)
    }
  }

  async function handleSendToAffida() {
    if (!contactId || affidaSubmissionLockedRef.current || sendingAffida) return
    affidaSubmissionLockedRef.current = true
    setSendingAffida(true)
    try {
      const affidaService = new InviaAffidaService()
      const data = await affidaService.send(contactId)
      if (data && data.success) {
        successToast('Inviato ad Affida con successo!')
        setInputs(prev => ({ ...prev, is_latest_valid_privacy_sent_to_affida: true }))
      } else {
        warningToast((data && (data.error || data.message)) || 'Errore durante l\'invio ad Affida')
      }
    } catch (error) {
      console.error('Error sending to Affida:', error)
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Errore durante l\'invio ad Affida'
      warningToast(msg)
    } finally {
      setSendingAffida(false)
      affidaSubmissionLockedRef.current = false
    }
  }

  async function load(showSpinner = true){
    try {
      if(showSpinner) setLoading(true)
      // Load products, campaigns, users, and agencies for forms
      await loadProducts()
      await loadInstitutes()
      await loadCampaigns()
      if (config.is_admin || config.is_agency_boss || config.is_agency_admin || config.is_internal_backoffice_manager || config.is_institute_backoffice_manager){
        await loadUsers()
        await loadAgencies()
      }
      
      if(!isNew){
        const response = await service.view(params.contact_id)
        const data = response.data || response
        setInputs({ ...emptyContact, ...data, assigned_agency: data.assigned_agency?.id, assigned_user: data.assigned_user?.email })
        setContactId(data.id)
        setFiles(data.files||[])
        setOpportunities(data.opportunities||[])
        setActiveOpportunity(data.opportunities?.find(item => item.state.state_type !== 'final') || null)
        setTrattenute(data.trattenute||[])
        
        // Extract phone number from contact_methods
        // if (data.contact_methods) {
        //   const phoneMethod = data.contact_methods.find(method => 
        //     method.type === 'cellulare' || method.type === 'telefone'
        //   )
        //   if (phoneMethod) {
        //     setInputs(prev => ({ ...prev, telefone: phoneMethod.value }))
        //   }
        // }

        // Detect assignment type for admin users
        if (config.is_admin) {
          if (data.assigned_user) {
            setAssignmentType('user')
            setSelectedUserId(data.assigned_user.email || data.assigned_user) // Handle both object and string
          } else if (data.assigned_agency) {
            setAssignmentType('agency')
            setSelectedAgencyId(data.assigned_agency.id || data.assigned_agency) // Handle both object and string
          } else {
            setAssignmentType('')
          }
        }
      } else {
        // Reset assignment type for new contacts
        setAssignmentType('')
      }
    } catch (error) {
      console.error('Error loading contact data:', error)
      warningToast('Errore durante il caricamento dei dati')
    } finally {
      if(showSpinner) setLoading(false)
    }
  }

  async function onSave(){
    try {
    if(isNew){
      const created = await service.create(inputs)
      setContactId(created.id)
        successToast('Contatto aggiunto con successo!')
        // Redirect to the detail page of the newly created contact
        history.push({
          pathname: `/contact-view/${created.id}`,
          state: location.state
        })
    } else {
      await service.save(contactId, inputs)
        successToast('Contatto aggiornato con successo!')
      }
    } catch (error) {
      console.error('Error saving contact:', error)
      warningToast(error.response?.data?.error || 'Errore durante il salvataggio del contatto')
    }
  }

  async function handleReassign(assignedUserId, assignedAgencyId) {
    try {
      console.log("Reassigning contact...", {
        contactId,
        assignedUserId,
        assignedAgencyId
      })
      
      const contactService = new ServicesRequest("people/contact")
      const result = await contactService.assignUser(
        contactId,
        assignedUserId || null,
        assignedAgencyId || null
      )
      
      console.log("Assignment result:", result)
      
      // Check if the result has an id (success) or is a successful response
      if (result && (result.id || result.success)) {
        successToast('Contatto riassegnato con successo')
        setShowReassignModal(false)
        setReassignType('')
        setReassignSelectedUserId('')
        setReassignSelectedAgencyId('')
        // Reload the contact data to update the assignment display
        await load(false)
      } else if (result && result.error) {
        // Handle specific error response
        warningToast('Errore: ' + result.error)
      } else {
        // Handle unexpected response format
        warningToast('Errore: Impossibile riassegnare il contatto')
      }
    } catch (error) {
      console.error('Error in handleReassign:', error)
      warningToast('Errore durante la riassegnazione')
    }
  }

  async function addComment(){
    if(contactId && newComment.trim().length>0){
      try {
      await service.addComment(contactId, newComment)
      setNewComment("")
        await load(false)
        successToast('Commento aggiunto con successo!')
      } catch (error) {
        console.error('Error adding comment:', error)
        warningToast('Errore durante l\'aggiunta del commento')
      }
    }
  }

  function handleKeyPress(e){
    if(e.key === 'Enter' && !e.shiftKey){
      e.preventDefault()
      addComment()
    }
  }

  function addFilesToQueue(fileList) {
    const selectedFiles = Array.from(fileList)
    if(selectedFiles.length > 0){
      const newQueueItems = selectedFiles.map(file => ({
        id: Date.now() + Math.random(),
        file: file,
        fileType: '',
        fileNote: '',
        status: 'pending', // pending, uploading, success, error
        error: null
      }))
      setUploadQueue(prev => [...prev, ...newQueueItems])
    }
  }

  function handleFileSelect(e){
    addFilesToQueue(e.target.files)
  }

  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = React.useRef(0)

  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    dragCounter.current = 0
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFilesToQueue(e.dataTransfer.files)
      e.dataTransfer.clearData()
    }
  }, [])

  function handleOpenUploadModal(){
    setShowUploadModal(true)
    setUploadQueue([])
    setIsUploading(false)
  }

  function handleCloseUploadModal(){
    if(isUploading){
      if(!window.confirm('Upload in corso. Sei sicuro di voler chiudere?')){
        return
      }
    }
    setShowUploadModal(false)
    setUploadQueue([])
    setIsUploading(false)
  }

  function removeFromQueue(itemId){
    setUploadQueue(prev => prev.filter(item => item.id !== itemId))
  }

  function updateQueueItem(itemId, updates){
    setUploadQueue(prev => prev.map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    ))
  }

  async function handleFileUpload(){
    if(!contactId) {
      warningToast('Salva il contatto prima di caricare documenti')
      return
    }

    const pendingItems = uploadQueue.filter(item => item.status === 'pending')
    
    if(pendingItems.length === 0) {
      warningToast('Nessun file da caricare')
      return
    }

    // Check if all items have file types
    const missingTypes = pendingItems.filter(item => !item.fileType)
    if(missingTypes.length > 0) {
      warningToast('Seleziona il tipo di documento per tutti i file')
      return
    }

    setIsUploading(true)
    let successCount = 0
    let errorCount = 0

    for(const item of pendingItems) {
      try {
        updateQueueItem(item.id, { status: 'uploading' })
        await service.upload(contactId, item.file, item.fileType, item.fileNote)
        updateQueueItem(item.id, { status: 'success' })
        successCount++
      } catch (error) {
        console.error('Error uploading file:', error)
        updateQueueItem(item.id, { 
          status: 'error', 
          error: error.message || 'Errore durante il caricamento'
        })
        errorCount++
      }
    }

    setIsUploading(false)

    if(successCount > 0) {
      await load(false)
      successToast(`${successCount} documento/i caricato/i con successo!`)
    }
    
    if(errorCount > 0) {
      warningToast(`${errorCount} documento/i non caricato/i`)
    }

    // Auto-close modal after upload completes
    handleCloseUploadModal()
  }

  async function handleCreateOpportunityWithoutQuote(data) {
    if(!contactId) {
      warningToast('Salva il contatto prima di creare opportunità')
      return
    }

    try {
      await service.createOpportunity({...data, contact: contactId})
      await load(false)
      setShowCreateOpportunityWithoutQuoteModal(false)
      successToast('Opportunità creata con successo!')
    } catch (error) {
      console.error('Error creating opportunity:', error)
      warningToast('Errore durante la creazione dell\'opportunità')
    }
  }

  async function handleCreateOpportunity(opportunityData){
    if(!contactId) {
      warningToast('Salva il contatto prima di creare opportunità')
      return
    }

    try {
      await service.createOpportunity({...opportunityData, contact: contactId})
      await load(false)
      setShowOpportunityModal(false)
      successToast('Opportunità creata con successo!')
    } catch (error) {
      console.error('Error creating opportunity:', error)
      warningToast('Errore durante la creazione dell\'opportunità')
    }
  }

  async function handleCreateTrattenuta(trattenutaData){
    if(!contactId) {
      warningToast('Salva il contatto prima di creare trattenute')
      return
    }

    try {
      await service.createTrattenuta({...trattenutaData, contact: contactId})
      await load(false)
      setShowTrattenutaModal(false)
      successToast('Trattenuta creata con successo!')
    } catch (error) {
      console.error('Error creating trattenuta:', error)
      warningToast('Errore durante la creazione della trattenuta')
    }
  }

  const [showDeleteTrattenutaModal, setShowDeleteTrattenutaModal] = useState(false)
  const [trattenutaToDelete, setTrattenutaToDelete] = useState(null)
  const [isDeletingTrattenuta, setIsDeletingTrattenuta] = useState(false)

  async function handleDeleteTrattenuta() {
    if (!trattenutaToDelete) return

    try {
      setIsDeletingTrattenuta(true)
      await service.deleteTrattenuta(trattenutaToDelete.id)
      await load(false)
      successToast('Trattenuta eliminata con successo')
      setShowDeleteTrattenutaModal(false)
      setTrattenutaToDelete(null)
    } catch (error) {
      console.error('Error deleting trattenuta:', error)
      warningToast('Errore durante l\'eliminazione della trattenuta')
    } finally {
      setIsDeletingTrattenuta(false)
    }
  }

  function handleStateChange(opportunity, newState) {
    if (newState && opportunity?.state && newState.state !== opportunity.state.state) {
      setSelectedOpportunity(opportunity)
      setSelectedNewState(newState)
      setShowStateChangeModal(true)
    }
  }

  async function handleConfirmStateChange() {
    if (!selectedOpportunity || !selectedNewState) {
      warningToast('Errore: dati mancanti per il cambio stato')
      return
    }

    try {
      const opportunityService = new OpportunitiesService()
      
      // Prepare the data for the API call
      const updateData = {
        state: selectedNewState.state
      }
      
      // Call the API to save the opportunity with the new state
      await opportunityService.save(selectedOpportunity.id, updateData)
      
      successToast('Stato opportunità aggiornato con successo!')
      
      // Reload data from server to get the updated state
      await load(false)
      
      // Close modal and reset state
      setShowStateChangeModal(false)
      setSelectedOpportunity(null)
      setSelectedNewState(null)
      
    } catch (error) {
      console.error('Error updating opportunity state:', error)
      warningToast('Errore durante l\'aggiornamento dello stato')
    }
  }

  // Helpers for contact status quick-change
  function formatTodayDate() {
    const d = new Date()
    return d.toISOString().split('T')[0]
  }

  function handleStatusSelect(e) {
    const val = e.target.value
    if (!val) return
    // if selecting Richiamo Concordato, prefill date with existing or today
    if (val === 'Richiamo Concordato') {
      let dateVal = inputs.data_per_richiamare;
      if (dateVal && typeof dateVal === 'string' && dateVal.includes('T')) {
          dateVal = dateVal.split('T')[0];
      }
      setPendingDataPerRichiamare(dateVal || formatTodayDate())
    } else {
      setPendingDataPerRichiamare('')
    }
    setPendingContactStatus(val)
    setShowContactStatusModal(true)
  }

  function handleCancelContactStatus() {
    setPendingContactStatus('')
    setPendingDataPerRichiamare('')
    setShowContactStatusModal(false)
  }

  async function handleConfirmContactStatus() {
    if (!pendingContactStatus) {
      setShowContactStatusModal(false)
      return
    }

    if (pendingContactStatus === 'Richiamo Concordato' && !pendingDataPerRichiamare) {
      warningToast('Seleziona una data per il richiamo')
      return
    }

    try {
      setSavingContactStatus(true)
      const payload = { contact_status: pendingContactStatus }
      if (pendingContactStatus === 'Richiamo Concordato') {
        payload.data_per_richiamare = pendingDataPerRichiamare
      } else {
        // clear the date if not Richiamo Concordato
        payload.data_per_richiamare = ''
      }

      // Ensure telefone is included to prevent deletion by backend
      if (inputs.telefone) {
        payload.telefone = inputs.telefone
      }

      if (contactId) {
        await service.save(contactId, payload)
        successToast('Stato contatto aggiornato')
        await load(false)
      } else {
        // unsaved contact: update local inputs and keep pending states
        setInputs(prev => ({ ...prev, contact_status: pendingContactStatus, data_per_richiamare: payload.data_per_richiamare }))
      }
    } catch (error) {
      console.error('Error updating contact status:', error)
      warningToast(error.response?.data?.error)
    } finally {
      setSavingContactStatus(false)
      setShowContactStatusModal(false)
      setPendingContactStatus('')
      setPendingDataPerRichiamare('')
    }
  }

  useEffect(()=>{ load() },[])

  if (loading && !isNew) {
    return (
      <Container fluid>
        <Row>
          <Col lg="12">
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                  <ReactLoading
                    type={"spin"}
                    color={"#007bff"}
                    height={50}
                    width={50}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <Container fluid>
    <Row>
      <Col lg="12">
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <div className="d-flex align-items-center justify-content-between">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb p-0 mb-0">
                  <li className="breadcrumb-item">
                    {fromOpportunities ? (
                      <Link to="/opportunity">Opportunità</Link>
                    ) : fromDashboard ? (
                      <Link to="/">Dashboard</Link>
                    ) : (
                      <Link to={{ pathname: "/contact", state: location.state || {}}}>Contatti</Link>
                    )}
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {getContactTitle()}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex align-items-center">
              {!isNew && (
                <Link to={`/contact-service-history/${contactId}`} className="btn btn-info btn-sm d-flex align-items-center justify-content-between me-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-clock-history" viewBox="0 0 16 16">
                    <path d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 0 1 .589.022l-.074.997zm2.004.45a7.003 7.003 0 0 0-.985-.299l.219-.976c.383.086.76.2 1.126.342l-.36.933zm1.37.71a7.01 7.01 0 0 0-.439-.27l.493-.87a8.025 8.025 0 0 1 .979.654l-.615.789a6.996 6.996 0 0 0-.418-.302zm1.834 1.79a6.99 6.99 0 0 0-.653-.796l.724-.69c.27.285.52.59.747.91l-.818.576zm.744 1.352a7.08 7.08 0 0 0-.214-.468l.893-.45a7.976 7.976 0 0 1 .45 1.088l-.95.313a7.023 7.023 0 0 0-.179-.483zm.53 2.507a6.991 6.991 0 0 0-.1-1.025l.985-.17c.067.386.106.778.116 1.17l-1 .025zm-.131 1.538c.033-.17.06-.339.081-.51l.993.123a7.957 7.957 0 0 1-.23 1.155l-.964-.267c.046-.165.086-.332.12-.501zm-.952 2.379c.184-.29.346-.594.486-.908l.914.405c-.16.36-.345.706-.555 1.038l-.845-.535zm-.964 1.205c.122-.122.239-.248.35-.378l.758.653a8.073 8.073 0 0 1-.401.432l-.707-.707z"/>
                    <path d="M8 1a7 7 0 1 0 4.95 11.95l.707.707A8.001 8.001 0 1 1 8 0v1z"/>
                    <path d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7.5 9V3z"/>
                  </svg>
                  <span className="ml-2">Storico Servizi</span>
                </Link>
              )}
            {fromOpportunities ? (
              <button 
                onClick={() => history.push({
                  pathname: '/opportunity',
                  state: {
                    filter: location.state?.opportunityFilter,
                    sort: location.state?.opportunitySort,
                    search: location.state?.opportunitySearch,
                    page: location.state?.opportunityPage
                  }
                })}
                className="btn btn-primary btn-sm d-flex align-items-center justify-content-between ml-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                <span className="ml-2">Indietro a Opportunità</span>
              </button>
            ) : fromDashboard ? (
              <button 
                onClick={() => history.push('/')}
                className="btn btn-primary btn-sm d-flex align-items-center justify-content-between ml-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                <span className="ml-2">Indietro a Dashboard</span>
              </button>
            ) : (
              <Link to={{ pathname: "/contact", state: location.state || {}}} className="btn btn-primary btn-sm d-flex align-items-center justify-content-between ml-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                <span className="ml-2">Indietro</span>
              </Link>
            )}
            {/* Quick contact status moved to main form */}
            </div>
          </div>
        </Col>
        <Col lg="12" className="mb-4 d-flex align-items-start">
          <div className="d-flex align-items-center flex-wrap">
            <h4 className="font-weight-bold d-flex align-items-center me-4 mb-2">
              {getContactTitle()}
            </h4>
            {/* Assignment Tags and Button */}
            <div className="d-flex flex-wrap align-items-center">
              {inputs.assigned_user && (
                <span className="badge bg-primary mx-1 mb-2 px-3 py-2 fs-6">
                  {(() => {
                    // Handle both object and email formats
                    if (typeof inputs.assigned_user === 'object' && inputs.assigned_user.name) {
                      return `${inputs.assigned_user.name} ${inputs.assigned_user.surname}`;
                    } else {
                      const user = users.find(u => u.email == inputs.assigned_user);
                      return user ? `${user.name} ${user.surname}` : `User: ${inputs.assigned_user}`;
                    }
                  })()}
                </span>
              )}
              {inputs.assigned_agency && (
                <span className="badge bg-info mx-1 mb-2 px-3 py-2 fs-6">
                  {(() => {
                    // Handle both object and ID formats
                    const agencyId = typeof inputs.assigned_agency === 'object' ? inputs.assigned_agency.id : inputs.assigned_agency;
                    const agency = agencies.find(a => a.id == agencyId);
                    if (agency) {
                      return agency.name;
                    } else if (typeof inputs.assigned_agency === 'object' && inputs.assigned_agency.name) {
                      return inputs.assigned_agency.name;
                    } else {
                      return `Agency: ${agencyId}`;
                    }
                  })()}
                </span>
              )}
              {!inputs.assigned_user && !inputs.assigned_agency && (
                <span className="badge bg-secondary mx-1 mb-2 px-3 py-2 fs-6">
                  Non assegnato
                </span>
              )}
              
              {/* Assign Button for Admin and Agency Boss */}
              {(config.is_admin || config.is_agency_boss) && !isNew && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="px-3 py-2 mx-1 mb-2"
                  onClick={() => {
                    setShowReassignModal(true)
                    setReassignType('')
                    setReassignSelectedUserId('')
                    setReassignSelectedAgencyId('')
                  }}
                >
                  <i className="fas fa-user-plus me-2"></i>
                  Riassegna
                </Button>
              )}

              {/* Criteri Button */}
              {!isNew && !servicesHidden && (
                <Button
                  variant="outline-info"
                  size="sm"
                  className="px-3 py-2 mx-1 mb-2"
                  onClick={() => {
                    history.push(`/criteri-assuntivi?contact_id=${contactId}`)
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                    <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z"/>
                  </svg>
                  Criteri
                </Button>
              )}

              {/* Preventivi Button */}
              {!isNew && !servicesHidden && (
                <Button
                  variant="outline-warning"
                  size="sm"
                  className="px-3 py-2 mx-1 mb-2"
                  onClick={() => {
                    history.push(`/preventivi?contact_id=${contactId}`)
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                    <path d="M1 3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1H1zm7 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
                    <path d="M0 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V5zm3 0a2 2 0 0 1-2 2v4a2 2 0 0 1 2 2h10a2 2 0 0 1 2-2V7a2 2 0 0 1-2-2H3z"/>
                  </svg>
                  Preventivi
                </Button>
              )}

              {/* Censimenti Button */}
              {!isNew && !servicesHidden && inputs.codice_fiscale_datore && (
                <Button
                  variant="outline-success"
                  size="sm"
                  className="px-3 py-2 mx-1 mb-2"
                  onClick={() => {
                    history.push(`/censimenti?contact_id=${contactId}`)
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
                  </svg>
                  Censimenti
                </Button>
              )}

              {/* Quota Button */}
              {!isNew && !servicesHidden && inputs.codice_fiscale && inputs.chiave_pensione && (
                <Button
                  variant="outline-dark"
                  size="sm"
                  className="px-3 py-2 mx-1 mb-2"
                  onClick={() => {
                    history.push(`/quota?contact_id=${contactId}`)
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
                  </svg>
                  Quota
                </Button>
              )}

              {/* Invia Privacy Button - hidden; privacy is managed via the backend templates. */}
              {/*
              {!isNew && !servicesHidden && inputs.telefone && (
                <Button 
                  variant="outline-info" 
                  size="sm"
                  className="px-3 py-2 mx-1 mb-2"
                  onClick={() => setShowPrivacyModal(true)}
                  disabled={sendingPrivacy}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                  </svg>
                  {sendingPrivacy ? 'Invio in corso...' : 'Invia Privacy'}
                </Button>
              )}
              */}

              {/* Invia A Affida Button - hidden; depends on the privacy workflow which is managed via backend templates. */}
              {/*
              {!isNew && !servicesHidden && (
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  className="px-3 py-2 mx-1 mb-2"
                  onClick={handleSendToAffida}
                  disabled={
                    sendingAffida ||
                    !inputs.is_latest_privacy_confirmed ||
                    inputs.is_latest_valid_privacy_sent_to_affida
                  }
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                    <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z"/>
                  </svg>
                  {sendingAffida ? 'Invio in corso...' : 'Invia A Affida'}
                </Button>
              )}
              */}

            </div>
          </div>
        </Col>
        <Col lg="12">
          <Card>
          <Card.Body>
              <Form className="g-3">
                <div className="row">
                  {/* Contact Status Section - Moved here for better visibility */}
                  <div className="col-12 mb-4">
                    <div className="p-3 border rounded bg-light-primary" style={{backgroundColor: '#f8f9fa', borderLeft: '5px solid #007bff'}}>
                        <div className="row align-items-center">
                            <div className="col-md-3">
                                <Form.Label className="form-label font-weight-bold text-uppercase text-primary mb-0" style={{fontSize: '1.1rem'}}>
                                    Stato Contatto
                                </Form.Label>
                                <div className="text-muted small">Aggiorna lo stato attuale del contatto</div>
                            </div>
                            <div className="col-md-9">
                                <div className="d-flex align-items-center justify-content-end">
                                    {(pendingContactStatus === 'Richiamo Concordato' || inputs.contact_status === 'Richiamo Concordato') && (
                                        <div className="me-3">
                                            <Form.Label className="mb-1 small text-muted">Data Richiamo</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={pendingContactStatus === 'Richiamo Concordato' ? (pendingDataPerRichiamare || '') : (inputs.data_per_richiamare || '')}
                                                onChange={(e) => {
                                                    if (pendingContactStatus === 'Richiamo Concordato') setPendingDataPerRichiamare(e.target.value)
                                                    else setInputs(prev => ({ ...prev, data_per_richiamare: e.target.value }))
                                                }}
                                                readOnly={savingContactStatus}
                                                style={{maxWidth: '200px'}}
                                            />
                                        </div>
                                    )}

                                    <div className="flex-grow-1" style={{maxWidth: '350px'}}>
                                        <Form.Label className="mb-1 small text-muted">Seleziona Stato</Form.Label>
                                        <Form.Control
                                            as="select"
                                            size="lg"
                                            value={pendingContactStatus || inputs.contact_status || ''}
                                            onChange={handleStatusSelect}
                                            disabled={savingContactStatus}
                                            style={{ fontWeight: 'bold' }}
                                        >
                                            <option value="">Seleziona Stato...</option>
                                            <option value="Contatto Attivo">Contatto Attivo</option>
                                            <option value="Contatto Non Interessato">Contatto Non Interessato</option>
                                            <option value="Non Risponde">Non Risponde</option>
                                            <option value="Richiamo Concordato">Richiamo Concordato</option>
                                            <option value="Contatto Interessato">Contatto Interessato</option>
                                            <option value="Mai Fattibile">Mai Fattibile</option>
                                            <option value="Maturazione TFR">Maturazione TFR</option>

                                        </Form.Control>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="name" className="form-label font-weight-bold text-muted text-uppercase">Nome</Form.Label>
                    <Form.Control 
                      type="text" 
                      className="form-control" 
                      name="name" 
                      value={inputs.name || ''} 
                      onChange={onChange}
                      readOnly={hasEditAccess(activeOpportunity) === false}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="surname" className="form-label font-weight-bold text-muted text-uppercase">Cognome</Form.Label>
                    <Form.Control 
                      type="text" 
                      className="form-control" 
                      name="surname" 
                      value={inputs.surname || ''} 
                      onChange={onChange}
                      readOnly={hasEditAccess(activeOpportunity) === false}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="email" className="form-label font-weight-bold text-muted text-uppercase">Email</Form.Label>
                    <Form.Control 
                      type="email" 
                      className="form-control" 
                      name="email" 
                      value={inputs.email || ''} 
                      onChange={onChange}
                      readOnly={hasEditAccess(activeOpportunity) === false}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="telefone" className="form-label font-weight-bold text-muted text-uppercase">Telefono</Form.Label>
                    <Form.Control 
                      type="tel" 
                      className="form-control" 
                      name="telefone" 
                      value={inputs.telefone || ''} 
                      onChange={onChange}
                      placeholder=""
                      readOnly={hasEditAccess(activeOpportunity) === false}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="codice_fiscale" className="form-label font-weight-bold text-muted text-uppercase">Codice Fiscale</Form.Label>
                    <Form.Control 
                      type="text" 
                      className="form-control" 
                      name="codice_fiscale" 
                      value={inputs.codice_fiscale || ''} 
                      onChange={onChange}
                      readOnly={hasEditAccess(activeOpportunity) === false}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="luogo_di_nascita" className="form-label font-weight-bold text-muted text-uppercase">Luogo di Nascita</Form.Label>
                    <Form.Control 
                      type="text" 
                      className="form-control" 
                      name="luogo_di_nascita" 
                      value={inputs.luogo_di_nascita || ''} 
                      onChange={onChange}
                      readOnly={hasEditAccess(activeOpportunity) === false}
                    />
                  </div>
                   <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="iban" className="form-label font-weight-bold text-muted text-uppercase">IBAN</Form.Label>
                    <Form.Control 
                      type="text" 
                      className="form-control" 
                      name="iban" 
                      value={inputs.iban || ''} 
                      onChange={onChange}
                      placeholder=""
                      readOnly={hasEditAccess(activeOpportunity) === false}
                    />
                  </div>
                   <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="medico_curante" className="form-label font-weight-bold text-muted text-uppercase">Medico Curante</Form.Label>
                    <Form.Control 
                      type="text" 
                      className="form-control" 
                      name="medico_curante" 
                      value={inputs.medico_curante || ''} 
                      onChange={onChange}
                      placeholder=""
                      readOnly={hasEditAccess(activeOpportunity) === false}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="preferenza_orario" className="form-label font-weight-bold text-muted text-uppercase">Preferenza Orario</Form.Label>
                    <Form.Control 
                      as="select"
                      className="form-control" 
                      name="preferenza_orario" 
                      value={inputs.preferenza_orario || ''} 
                      onChange={onChange}
                      readOnly={hasEditAccess(activeOpportunity) === false}
                    >
                      <option value="">Seleziona orario</option>
                      <option value="Mattina">Mattina</option>
                      <option value="Pomeriggio">Pomeriggio</option>
                      <option value="Sera">Sera</option>
                    </Form.Control>
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="residenza" className="form-label font-weight-bold text-muted text-uppercase">Residenza</Form.Label>
                    <Form.Control 
                      type="text" 
                      className="form-control" 
                      name="residenza" 
                      value={inputs.residenza || ''} 
                      onChange={onChange}
                      readOnly={hasEditAccess(activeOpportunity) === false}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="categoria_di_lavoro" className="form-label font-weight-bold text-muted text-uppercase">Categoria di Lavoro</Form.Label>
                    <Form.Control as="select" className="form-control" name="categoria_di_lavoro" value={inputs.categoria_di_lavoro || ''} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false}>
                      <option value="">Seleziona categoria</option>
                      <option value="pubblico">Pubblico</option>
                      <option value="statale">Statale</option>
                      <option value="privato">Privato</option>
                      <option value="pensionato">Pensionato</option>
                    </Form.Control>
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="has_maturing_trattenuta" className="form-label font-weight-bold text-muted text-uppercase">Contattare per Rinnovo</Form.Label>
                    <div className="form-control" style={{ border: 'none', paddingLeft: 0 }}>
                      <Form.Check 
                        type="checkbox"
                        id="has_maturing_trattenuta"
                        label="Sì"
                        checked={!!inputs.has_maturing_trattenuta}
                        disabled={true}
                      />
                    </div>
                  </div>
                  
                  {/* New backend fields - display all */}
                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="codice_fiscale_datore" className="form-label font-weight-bold text-muted text-uppercase">Codice Fiscale Datore</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-control"
                      name="codice_fiscale_datore"
                      value={inputs.codice_fiscale_datore || ''}
                      onChange={onChange}
                      readOnly={hasEditAccess(activeOpportunity) === false}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="cittadinanza" className="form-label font-weight-bold text-muted text-uppercase">Cittadinanza</Form.Label>
                    <Form.Control as="select" className="form-control" name="cittadinanza" value={inputs.cittadinanza || ''} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false}>
                      <option value="">Seleziona</option>
                      <option value="ITA">Italiana</option>
                      <option value="STR">Straniera</option>
                    </Form.Control>
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="residente_in_it" className="form-label font-weight-bold text-muted text-uppercase">Residente In Italia (data)</Form.Label>
                    <Form.Control type="date" className="form-control" name="residente_in_it" value={inputs.residente_in_it || ''} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} />
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="sesso" className="form-label font-weight-bold text-muted text-uppercase">Sesso</Form.Label>
                    <Form.Control as="select" className="form-control" name="sesso" value={inputs.sesso || ''} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false}>
                      <option value="">Seleziona</option>
                      <option value="M">M</option>
                      <option value="F">F</option>
                    </Form.Control>
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="data_di_nascita" className="form-label font-weight-bold text-muted text-uppercase">Data Di Nascita</Form.Label>
                    <Form.Control type="date" className="form-control" name="data_di_nascita" value={inputs.data_di_nascita || ''} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} />
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="data_di_assunzione" className="form-label font-weight-bold text-muted text-uppercase">Data Di Assunzione</Form.Label>
                    <Form.Control type="date" className="form-control" name="data_di_assunzione" value={inputs.data_di_assunzione || ''} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} />
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Label htmlFor="is_part_time" className="form-label font-weight-bold text-muted text-uppercase">Part Time</Form.Label>
                    <Form.Control as="select" className="form-control" name="is_part_time" value={inputs.is_part_time} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false}>
                      <option value="">Seleziona</option>
                      <option value="on">Si</option>
                      <option value="off">No</option>
                    </Form.Control>
                  </div>

                  <div className="col-md-4 mb-3">
                    <Form.Label htmlFor="part_time_percentage" className="form-label font-weight-bold text-muted text-uppercase">Percentuale Part Time (%)</Form.Label>
                    <Form.Control type="number" className="form-control" name="part_time_percentage" step="0.1" onWheel={(e) => e.target.blur()} value={inputs.part_time_percentage || 0} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} min={0} max={100} />
                  </div>

                  <div className="col-md-4 mb-3">
                    <Form.Label htmlFor="retribuzione" className="form-label font-weight-bold text-muted text-uppercase">Retribuzione</Form.Label>
                    <CurrencyInput step="1" onWheel={(e) => e.target.blur()} className="form-control" name="retribuzione" value={inputs.retribuzione || 0} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} />
                  </div>

                  <div className="col-md-4 mb-3">
                    <Form.Label htmlFor="chiave_pensione" className="form-label font-weight-bold text-muted text-uppercase">Chiave Pensione</Form.Label>
                    <Form.Control type="number" step="1" onWheel={(e) => e.target.blur()} className="form-control" name="chiave_pensione" value={inputs.chiave_pensione || 0} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} />
                  </div>

                  <div className="col-md-4 mb-3">
                    <Form.Label htmlFor="mensilita" step="0.1" onWheel={(e) => e.target.blur()} className="form-label font-weight-bold text-muted text-uppercase">Mensilità</Form.Label>
                    <Form.Control as="select" className="form-control" name="mensilita" value={inputs.mensilita} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} >
                        <option value="">Seleziona</option>
                        <option value="13">13</option>
                        <option value="14">14</option>
                    </Form.Control>
                  </div>

                  <div className="col-md-4 mb-3">
                    <Form.Label htmlFor="tfr" className="form-label font-weight-bold text-muted text-uppercase">TFR</Form.Label>
                    <CurrencyInput step="0.1" onWheel={(e) => e.target.blur()} className="form-control" name="tfr" value={inputs.tfr || 0} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} />
                  </div>

                  <div className="col-md-4 mb-3">
                    <Form.Label htmlFor="tfr_data_riferimento" className="form-label font-weight-bold text-muted text-uppercase">TFR Data Riferimento</Form.Label>
                    <Form.Control type="date" className="form-control" name="tfr_data_riferimento" value={inputs.tfr_data_riferimento || ''} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} />
                  </div>

                  <div className="col-md-4 mb-3">
                    <Form.Label htmlFor="is_tempo_indeterminato" className="form-label font-weight-bold text-muted text-uppercase">Tempo Indeterminato</Form.Label>
                    <div>
                        <Form.Control as="select" name="is_tempo_indeterminato" id="is_tempo_indeterminato" value={inputs.is_tempo_indeterminato} onChange={onChange} disabled={hasEditAccess(activeOpportunity) === false} >
                            <option value="">Seleziona</option>
                            <option value="on">Si</option>
                            <option value="off">No</option>
                        </Form.Control>
                    </div>
                  </div>

                  <div className="col-md-4 mb-3">
                    <Form.Label htmlFor="malattia" className="form-label font-weight-bold text-muted text-uppercase">Malattia oltre 10 Giorni in Busta Paga</Form.Label>
                    <Form.Control as="select" className="form-control" name="malattia" value={inputs.malattia} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} >
                        <option value="">Seleziona</option>
                        <option value="on">Si</option>
                        <option value="off">No</option>
                    </Form.Control>
                  </div>

                  <div className="col-md-4 mb-3">
                    <Form.Label htmlFor="cig_oltre_40_ore" className="form-label font-weight-bold text-muted text-uppercase">CIG oltre 40 ore</Form.Label>
                    <Form.Control as="select" className="form-control" name="cig_oltre_40_ore" value={inputs.cig_oltre_40_ore} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} >
                        <option value="">Seleziona</option>
                        <option value="on">Si</option>
                        <option value="off">No</option>
                    </Form.Control>
                  </div>

                  <div className="col-md-4 mb-3">
                    <Form.Label htmlFor="quinto_cedibile" className="form-label font-weight-bold text-muted text-uppercase">Quinto Cedibile</Form.Label>
                    <CurrencyInput step="0.1" onWheel={(e) => e.target.blur()} className="form-control" name="quinto_cedibile" value={inputs.quinto_cedibile} onChange={onChange} readOnly={hasEditAccess(activeOpportunity) === false} />
                  </div>
                  {!isNew && inputs.last_updated && (
                    <div className="col-md-6 mb-3">
                      <Form.Label htmlFor="last_updated" className="form-label font-weight-bold text-muted text-uppercase">Ultimo Aggiornamento</Form.Label>
                      <Form.Control 
                        type="text" 
                        className="form-control" 
                        name="last_updated" 
                        value={new Date(inputs.last_updated).toLocaleString('it-IT')} 
                        readOnly
                      />
                    </div>
                  )}
                  {!isNew && inputs.last_updated && (
                    <div className="col-md-6 mb-3">
                      <Form.Label htmlFor="created_at" className="form-label font-weight-bold text-muted text-uppercase">Creato il</Form.Label>
                      <Form.Control 
                        type="text" 
                        className="form-control" 
                        name="created_at" 
                        value={new Date(inputs.created_at).toLocaleString('it-IT')} 
                        readOnly
                      />
                    </div>
                  )}
                  {/* Assignment Display and Reassignment Button */}
                  <div className="col-md-12 mb-3" hidden={true}>
                    <Form.Label className="form-label font-weight-bold text-muted text-uppercase">Assegnazione</Form.Label>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        {inputs.assigned_user ? (
                          <span className="text-success">
                            <strong>Assegnato a:</strong> {users.find(u => u.email == inputs.assigned_user)?.name} {users.find(u => u.email == inputs.assigned_user)?.surname}
                          </span>
                        ) : inputs.assigned_agency ? (
                          <span className="text-success">
                            <strong>Assegnato all'agenzia:</strong> {agencies.find(a => a.id == inputs.assigned_agency)?.name}
                          </span>
                        ) : (
                          <span className="text-muted">
                            <strong>Non assegnato</strong>
                          </span>
                        )}
                      </div>
                      {(config.is_admin || config.is_agency_boss) && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setShowReassignModal(true)
                            setReassignType('')
                            setReassignSelectedUserId('')
                            setReassignSelectedAgencyId('')
                          }}
                        >
                          Riassegna
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {hasEditAccess(activeOpportunity) && (<div className="mt-4 d-flex gap-2">
                  <Button onClick={onSave}>
                    {isNew ? 'Aggiungi' : 'Salva'}
                  </Button>
                </div>)}

                {/* Opportunities Section */}
                <Row className="mt-4">
                  <Col lg="12">
                    <h5 className="mb-3">Opportunità</h5>
                    <Button 
                      variant="primary" 
                      size="sm" 
                      className="mb-3"
                      onClick={() => setShowCreateOpportunityWithoutQuoteModal(true)}
                    >
                      Crea Oppurtunita Senza Preventivo
                    </Button>

                    <div className="table-responsive">
                      <Table bordered size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '13%' }}>Prodotto</th>
                            <th style={{ width: '10%' }}>Istituto</th>
                            <th style={{ width: '10%' }}>Campagna</th>
                            <th style={{ width: '10%' }}>Stato</th>
                            <th style={{ width: '12%' }}>Data Creazione</th>
                            <th style={{ width: '12%' }}>Ultimo Aggiornamento</th>
                            <th style={{ width: '22%' }}>Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const accessibleOpportunities = (opportunities || []).filter(opp => hasReadAccess(opp))
                            const totalOpportunities = (opportunities || []).length
                            const hasInaccessibleOpportunities = totalOpportunities > 0 && accessibleOpportunities.length === 0
                            
                            if (hasInaccessibleOpportunities) {
                              return (
                                <tr>
                                  <td colSpan="8" className="text-center text-warning py-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="mb-2">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    <div>Il contatto ha opportunità fuori dal tuo ambito di lettura</div>
                                  </td>
                                </tr>
                              )
                            }
                            
                            if (accessibleOpportunities.length === 0) {
                              return (
                                <tr>
                                  <td colSpan="8" className="text-center text-muted py-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="mb-2">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <div>Nessuna opportunità</div>
                                  </td>
                                </tr>
                              )
                            }
                            
                            return accessibleOpportunities.map(opp => (
                              <tr key={opp.id}>
                                <td>{opp.product?.name || '-'}</td>
                                <td>{opp.institute?.name || '-'}</td>
                                <td>{opp.campaign?.name || '-'}</td>
                                <td>
                                  <span className={`badge ${
                                    opp.state?.state === '1' ? 'bg-primary' :
                                    opp.state?.state === '13' ? 'bg-warning' :
                                    opp.state?.state === '12' || opp.state?.state === 'vinto' ? 'bg-success' :
                                    opp.state?.state === '10' || opp.state?.state === 'archiviato' ? 'bg-secondary' :
                                    'bg-info'
                                  }`}>
                                    {opp.state?.name || opp.state || 'Stato non disponibile'}
                                  </span>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {opp.creation_date ? new Date(opp.creation_date).toLocaleDateString('it-IT') : '-'}
                                  </small>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {opp.last_updated ? new Date(opp.last_updated).toLocaleDateString('it-IT') : '-'}
                                  </small>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      className="me-2"
                                      onClick={() => {
                                        setViewingOpportunity(opp)
                                        setShowOpportunityModal(true)
                                      }}
                                      title="Dettagli"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                        <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z"/>
                                        <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"/>
                                      </svg>
                                    </Button>
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      className="me-2"
                                      onClick={() => {
                                        history.push(`/criteri-assuntivi?contact_id=${contactId}&opportunity_id=${opp.id}&next=preventivi`)
                                      }}
                                      title="Rerun Criteri & Preventivi"
                                      disabled={opp?.state?.state_type === 'final'}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                                        <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                                    </svg>
                                    </Button>
                                    <Button
                                      variant="outline-dark"
                                      size="sm"
                                      className="me-2"
                                      onClick={() => handleShowHistory(opp)}
                                      title="Cronologia stati"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-clock-history" viewBox="0 0 16 16">
                                        <path d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 0 1 .589.022l-.074.997zm2.004.45a7.003 7.003 0 0 0-.985-.299l.219-.976c.383.086.76.2 1.126.342l-.36.933zm1.37.71a7.01 7.01 0 0 0-.439-.27l.493-.87a8.025 8.025 0 0 1 .979.654l-.615.789a6.996 6.996 0 0 0-.418-.302zm1.834 1.79a6.99 6.99 0 0 0-.653-.796l.724-.69c.27.285.52.59.747.91l-.818.576zm.744 1.352a7.08 7.08 0 0 0-.214-.468l.893-.45a7.976 7.976 0 0 1 .45 1.088l-.95.313a7.023 7.023 0 0 0-.179-.483zm.53 2.507a6.991 6.991 0 0 0-.1-1.025l.985-.17c.067.386.106.778.116 1.17l-1 .025zm-.131 1.538c.033-.17.06-.339.081-.51l.993.123a7.957 7.957 0 0 1-.23 1.155l-.964-.267c.046-.165.086-.332.12-.501zm-.952 2.379c.184-.29.346-.594.486-.908l.914.405c-.16.36-.345.706-.555 1.038l-.845-.535zm-.964 1.205c.122-.122.239-.248.35-.378l.758.653a8.073 8.073 0 0 1-.401.432l-.707-.707z"/>
                                        <path d="M8 1a7 7 0 1 0 4.95 11.95l.707.707A8.001 8.001 0 1 1 8 0v1z"/>
                                        <path d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7.5 9V3z"/>
                                      </svg>
                                    </Button>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      className="me-2"
                                      onClick={() => handleGoBack(opp)}
                                      title="Torna allo stato precedente"
                                      disabled={opp?.state?.state_type === 'final' || opp?.state?.state === '1'}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2v1z"/>
                                        <path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466z"/>
                                      </svg>
                                    </Button>
                                    {hasEditAccess(opp) ? (
                                      <>
                                        <Form.Control
                                          as="select"
                                          size="sm"
                                          value=""
                                          onChange={(e) => {
                                            const selectedState = opp.state?.next_states?.find(s => s.state === e.target.value)
                                            if (selectedState) {
                                              handleStateChange(opp, selectedState)
                                            }
                                          }}
                                          style={{ minWidth: '120px' }}
                                        >
                                          <option value="">Seleziona stato</option>
                                          {(opp.state?.next_states || [])
                                            .filter(state => {
                                              if (state.state === '8A') return inputs.categoria_di_lavoro !== 'pensionato';
                                              if (state.state === '8B') return inputs.categoria_di_lavoro === 'pensionato';
                                              return true;
                                            })
                                            .map(state => (
                                            <option key={state.id} value={state.state}>
                                              {state.name}
                                            </option>
                                          ))}
                                        </Form.Control>
                                      </>
                                    ) : (
                                      <span className="text-muted">
                                        <small>Nessun accesso di modifica</small>
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          })()}
                        </tbody>
                      </Table>
                    </div>
              </Col>
                </Row>

                {/* Trattenute Section */}
                <Row className="mt-4">
                  <Col lg="12">
                    <h5 className="mb-3">Impegni Gia In Corso (In Busta Paga)</h5>
                    
                    {hasEditAccess(activeOpportunity) && (<div className="mb-3">
                      <Button 
                        variant="outline-primary" 
                        onClick={() => setShowTrattenutaModal(true)}
                        disabled={!contactId}
                        className="d-flex align-items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="me-2">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Aggiungi Impegno
                      </Button>
                      {!contactId && (
                        <small className="text-muted d-block mt-1">
                          Salva il contatto prima di aggiungere impegni
                        </small>
                      )}
                    </div>)}

                    <div className="table-responsive">
                      <Table bordered size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '15%' }}>Tipo</th>
                            <th style={{ width: '10%' }}>Durata</th>
                            <th style={{ width: '15%' }}>Rata</th>
                            <th style={{ width: '15%' }}>Residuo</th>
                            <th style={{ width: '15%' }}>Data Inizio</th>
                            <th style={{ width: '15%' }}>Stato</th>
                            <th style={{ width: '15%' }}>Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(trattenute || []).length === 0 ? (
                            <tr>
                              <td colSpan="7" className="text-center text-muted py-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="mb-2">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 01-2 2z" />
                                </svg>
                                <div>Nessuna trattenuta</div>
                              </td>
                            </tr>
                          ) : (
                            (trattenute || []).map(trat => {
                              const residuo = residuoRateResidue(trat)
                              return (
                              <tr key={trat.id}>
                                <td>{trat.tipo || '-'}</td>
                                <td>{trat.duration !== 0 ? `${trat.duration} mesi` : '-'}</td>
                                <td>{trat.amount !== '0.00' ? formatCurrency(trat.amount, 0) : '-'}</td>
                                <td>{residuo ? formatCurrency(residuo, 1) : '-'}</td>
                                <td>
                                  <small className="text-muted">
                                    {trat.start_date ? new Date(trat.start_date).toLocaleDateString('it-IT') : '-'}
                                  </small>
                                </td>
                                <td>
                                  <span className={`badge ${trat.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                    {trat.is_active ? 'Attivo' : 'Inattivo'}
                                  </span>
                                </td>
                                <td>
                                  {hasEditAccess(activeOpportunity) && (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => {
                                        setTrattenutaToDelete(trat)
                                        setShowDeleteTrattenutaModal(true)
                                      }}
                                      title="Elimina"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                      </svg>
                                    </Button>
                                  )}
                                </td>
                              </tr>
                              )
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col md="6">
                    <h5 className="mb-3">Commenti</h5>
                    
                    {/* Add Comment Form */}
                    <div className="mb-3">
                      <div className="d-flex align-items-end">
                        <div className="flex-grow-1 me-2">
                          <Form.Control 
                            type="text"
                            value={newComment || ''} 
                            onChange={(e) => setNewComment(e.target.value)} 
                            onKeyPress={handleKeyPress}
                            placeholder="Scrivi un commento..."
                            disabled={!contactId}
                          />
                        </div>
                        <Button 
                          variant="primary"
                          onClick={addComment} 
                          disabled={!contactId || newComment.trim().length === 0}
                          className="mb-0 px-3 py-2"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" transform="rotate(45 12 12)" />
                          </svg>
                        </Button>
                </div>
                      <small className="text-muted">
                        {contactId ? "" : "Salva il contatto prima di aggiungere commenti"}
                      </small>
                    </div>

                    {/* Comments List */}
                    <div className="comments-container" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {(inputs.comments || []).length === 0 ? (
                        <div className="text-center text-muted py-3">
                          <svg xmlns="http://www.w3.org/2000/svg" width="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="mb-2">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <p className="mb-0">Nessun commento ancora</p>
                        </div>
                      ) : (
                        <div className="list-group list-group-flush">
                          {(inputs.comments || []).map(c => (
                            <div key={c.id} className="list-group-item px-0 py-2 border-0">
                              <div className="d-flex align-items-start">
                                
                                <div className="flex-grow-1">
                                  <div className="d-flex justify-content-between align-items-start mb-1">
                                    <small className="text-muted fw-bold">{c.created_by}</small>
                                    <small className="text-muted">
                                      {c.created_at ? new Date(c.created_at).toLocaleString('it-IT', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }+ "  ") : ''}
                                    </small>
                                  </div>
                                  <div className="comment-text" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                                    {c.text}
                                  </div>
                                </div>
                              </div>
                    </div>
                  ))}
                        </div>
                      )}
                </div>
              </Col>
              <Col md="6">
                    <h5 className="mb-3">Documenti</h5>
                    
                    {/* Upload Button */}
                    {hasEditAccess(activeOpportunity) && (
                        <div className="mb-3">
                          <Button
                            variant="outline-primary"
                            onClick={handleOpenUploadModal}
                            disabled={!contactId}
                            className="d-flex align-items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="me-2">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Carica Documento
                        </Button>
                        {!contactId && (
                          <small className="text-muted d-block mt-1">
                            Salva il contatto prima di caricare documenti
                          </small>
                        )}
                       </div>)}

                    {/* Documents Table */}
                    <div className="table-responsive">
                      <Table bordered size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '28%' }}>Nome File</th>
                            <th style={{ width: '15%' }}>Tipo</th>
                            <th style={{ width: '15%' }}>Stato</th>
                            <th style={{ width: '22%' }}>Note</th>
                            <th style={{ width: '20%' }}>Data Caricamento</th>
                          </tr>
                        </thead>
                  <tbody>
                          {(files || []).length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center text-muted py-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="mb-2">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                <div>Nessun documento caricato</div>
                              </td>
                            </tr>
                          ) : (
                            (files || []).map(f => (
                              <tr key={f.id}>
                                <td>
                                  <a 
                                    href={f.url} 
                                    download={f.filename} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className="text-decoration-none"
                                    onClick={(e) => {
                                      // Check if file exists by trying to fetch it
                                      fetch(f.url, { method: 'HEAD' })
                                        .then(response => {
                                          if (!response.ok) {
                                            e.preventDefault();
                                            alert('File not found: ' + f.filename);
                                          }
                                        })
                                        .catch(() => {
                                          e.preventDefault();
                                          alert('File not found: ' + f.filename);
                                        });
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="me-2">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    {f.filename.length > 25
                                      ? 
                                      (f.filename.substring(0, 25)+"...") : f.filename}
                                  </a>
                                </td>
                                <td>
                                  <span className="badge bg-secondary">
                                    {f.file_type || 'Non specificato'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${
                                    f.status === 'pending' ? 'bg-warning' :
                                    f.status === 'processing' ? 'bg-info' :
                                    f.status === 'awaiting_validation' ? 'bg-primary' :
                                    f.status === 'validated' ? 'bg-success' :
                                    'bg-secondary'
                                  }`}>
                                    {f.status === 'pending' ? 'In attesa' :
                                     f.status === 'processing' ? 'In elaborazione' :
                                     f.status === 'awaiting_validation' ? 'In validazione' :
                                     f.status === 'validated' ? 'Validato' :
                                     f.status || 'Non specificato'}
                                  </span>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {f.note || '-'}
                                  </small>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString('it-IT', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }) : '-'}
                                  </small>
                                </td>
                              </tr>
                            ))
                          )}
                  </tbody>
                </Table>
                    </div>
              </Col>
            </Row>

                
              </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>

      {/* Privacy Modal */}
      <Modal show={showPrivacyModal} onHide={() => !sendingPrivacy && setShowPrivacyModal(false)} centered>
        <Modal.Header closeButton={!sendingPrivacy}>
          <Modal.Title>Invia Privacy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Data Appuntamento <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={privacyDate}
                onChange={(e) => setPrivacyDate(e.target.value)}
                autoFocus
                disabled={sendingPrivacy}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Ora Appuntamento <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="time"
                value={privacyTime}
                onChange={(e) => setPrivacyTime(e.target.value)}
                disabled={sendingPrivacy}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Note (Solo per Affida, nascosto al cliente)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Aggiungi una nota opzionale..."
                value={privacyNote}
                onChange={(e) => setPrivacyNote(e.target.value)}
                disabled={sendingPrivacy}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowPrivacyModal(false); setPrivacyNote(''); }} disabled={sendingPrivacy}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleSendPrivacy} disabled={!privacyDate || !privacyTime || sendingPrivacy}>
            {sendingPrivacy ? 'Invio in corso...' : 'Conferma e Invia'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* File Upload Modal */}
      <Modal show={showUploadModal} onHide={handleCloseUploadModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Carica Documenti</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="font-weight-bold">Seleziona File</Form.Label>
              <div
                onDragEnter={!isUploading ? handleDragEnter : undefined}
                onDragLeave={!isUploading ? handleDragLeave : undefined}
                onDragOver={!isUploading ? handleDragOver : undefined}
                onDrop={!isUploading ? handleDrop : undefined}
                style={{
                  border: `2px dashed ${isDragging ? 'var(--primary)' : '#dee2e6'}`,
                  borderRadius: '12px',
                  padding: '32px 20px',
                  textAlign: 'center',
                  backgroundColor: isDragging ? 'rgba(var(--primary-rgb, 59,130,246), 0.06)' : '#f8f9fa',
                  transition: 'all 0.2s ease',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  opacity: isUploading ? 0.6 : 1
                }}
                onClick={() => !isUploading && document.getElementById('customFileInput').click()}
              >
                <input
                  type="file"
                  id="customFileInput"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  multiple
                  disabled={isUploading}
                  style={{ display: 'none' }}
                />
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" viewBox="0 0 24 24" stroke={isDragging ? 'var(--primary)' : '#6c757d'} style={{ marginBottom: '8px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="mb-1 font-weight-bold" style={{ color: isDragging ? 'var(--primary)' : '#495057' }}>
                  {isDragging ? 'Rilascia i file qui' : 'Trascina i file qui'}
                </p>
                <small className="text-muted">
                  oppure <span style={{ color: 'var(--primary)', textDecoration: 'underline' }}>sfoglia</span> per selezionare
                </small>
                <br />
                <small className="text-muted">
                  PDF, DOC, DOCX, JPG, PNG, GIF
                </small>
              </div>
            </Form.Group>

            {uploadQueue.length > 0 && (
              <div className="mt-4">
                <h6 className="mb-3">Coda di Upload ({uploadQueue.length} file)</h6>
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <Table bordered size="sm" className="mb-0">
                    <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ width: '30%' }}>Nome File</th>
                        <th style={{ width: '20%' }}>Tipo Documento</th>
                        <th style={{ width: '25%' }}>Note</th>
                        <th style={{ width: '15%' }}>Stato</th>
                        <th style={{ width: '5%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadQueue.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '250px' }} title={item.file.name}>
                              {item.file.name}
                            </div>
                            <small className="text-muted">
                              ({(item.file.size / 1024).toFixed(1)} KB)
                            </small>
                          </td>
                          <td>
                            {item.status === 'success' ? (
                              <span className="badge bg-secondary">{item.fileType}</span>
                            ) : (
                              <Form.Control 
                                as="select"
                                size="sm"
                                value={item.fileType || ''}
                                onChange={(e) => updateQueueItem(item.id, { fileType: e.target.value })}
                                disabled={isUploading || item.status === 'uploading'}
                                required
                              >
                                <option value="">Seleziona tipo</option>
                                <option value="Anagrafiche">Anagrafiche</option>
                                <option value="Reddituale">Reddituale</option>
                                <option value="Altro">Altro</option>
                              </Form.Control>
                            )}
                          </td>
                          <td>
                            {item.status === 'success' ? (
                              <small className="text-muted">{item.fileNote || '-'}</small>
                            ) : (
                              <Form.Control 
                                as="textarea"
                                rows={1}
                                size="sm"
                                value={item.fileNote || ''}
                                onChange={(e) => updateQueueItem(item.id, { fileNote: e.target.value })}
                                placeholder="Note..."
                                disabled={isUploading || item.status === 'uploading'}
                              />
                            )}
                          </td>
                          <td>
                            {item.status === 'success' && (
                              <span className="badge bg-success">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" className="me-1">
                                  <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                                </svg>
                                Caricato
                              </span>
                            )}
                            {item.status === 'uploading' && (
                              <span className="badge bg-info">
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Caricamento...
                              </span>
                            )}
                            {item.status === 'error' && (
                              <span className="badge bg-danger" title={item.error}>
                                Errore
                              </span>
                            )}
                            {item.status === 'pending' && (
                              <span className="badge bg-secondary">In attesa</span>
                            )}
                          </td>
                          <td>
                            {item.status === 'pending' && (
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => removeFromQueue(item.id)}
                                disabled={isUploading}
                                title="Rimuovi"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                  <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                                </svg>
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseUploadModal} disabled={isUploading}>
            {isUploading ? 'Chiudi' : 'Annulla'}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleFileUpload}
            disabled={uploadQueue.length === 0 || isUploading || uploadQueue.filter(i => i.status === 'pending').length === 0}
          >
            {isUploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Caricamento...
              </>
            ) : (
              `Carica ${uploadQueue.filter(i => i.status === 'pending').length} File`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Opportunity Creation Modal */}
      <Modal show={showOpportunityModal} onHide={() => { setShowOpportunityModal(false); setViewingOpportunity(null); }} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{viewingOpportunity ? 'Dettagli Opportunità' : 'Nuova Opportunità'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <OpportunityForm 
            onSubmit={handleCreateOpportunity} 
            onClose={() => { setShowOpportunityModal(false); setViewingOpportunity(null); }} 
            products={products} 
            institutes={institutes}
            campaigns={campaigns}
            initialData={viewingOpportunity}
          />
        </Modal.Body>
      </Modal>

      {/* Trattenuta Creation Modal */}
      <Modal show={showTrattenutaModal} onHide={() => setShowTrattenutaModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Nuova Trattenuta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TrattenutaForm onSubmit={handleCreateTrattenuta} onClose={() => setShowTrattenutaModal(false)} />
        </Modal.Body>
      </Modal>

      {/* Reassignment Modal */}
      <Modal show={showReassignModal} onHide={() => setShowReassignModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Riassegna Contatto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Riassegna il contatto <strong>{inputs.name} {inputs.surname}</strong>:</p>
          
          {/* Assignment Type Selection - Only for Admin */}
          {config.is_admin && (
            <div className="mb-3">
              <Form.Label>Tipo di Assegnazione:</Form.Label>
              <Form.Control
                as="select"
                value={reassignType || ''}
                onChange={(e) => setReassignType(e.target.value)}
              >
                <option value="">Seleziona tipo...</option>
                <option value="user">Assegna a Utente</option>
                <option value="agency">Assegna a Agenzia</option>
              </Form.Control>
            </div>
          )}

          {/* User Assignment - Available for Agency Boss and Admin */}
          <div className="mb-3">
            <Form.Label>Assegna a Utente:</Form.Label>
            <Form.Control
              as="select"
              id="reassign-user"
              value={reassignSelectedUserId || ''}
              disabled={config.is_admin && reassignType !== 'user'}
              onChange={(e) => setReassignSelectedUserId(e.target.value)}
            >
              <option value="">Seleziona utente...</option>
              {users.map(user => (
                <option key={user.email} value={user.email}>
                  {user.name} {user.surname}
                </option>
              ))}
            </Form.Control>
            {config.is_agency_boss && !config.is_admin && (
              <small className="text-muted">
                Puoi assegnare solo agli utenti della tua agenzia
              </small>
            )}
            {config.is_admin && reassignType !== 'user' && (
              <small className="text-muted">
                Seleziona "Assegna a Utente" per abilitare questa opzione
              </small>
            )}
            {reassignSelectedUserId && (
              <div className="mt-2">
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => handleReassign(reassignSelectedUserId, null)}
                >
                  Assegna a {users.find(u => u.email == reassignSelectedUserId)?.name} {users.find(u => u.email == reassignSelectedUserId)?.surname}
                </Button>
              </div>
            )}
          </div>

          {/* Agency Assignment - Only available for Admin */}
          {config.is_admin && (
            <div className="mb-3">
              <Form.Label>Assegna a Agenzia:</Form.Label>
              <Form.Control
                as="select"
                id="reassign-agency"
                value={reassignSelectedAgencyId || ''}
                disabled={reassignType !== 'agency'}
                onChange={(e) => setReassignSelectedAgencyId(e.target.value)}
              >
                <option value="">Seleziona agenzia...</option>
                {agencies.map(agency => (
                  <option key={agency.id} value={agency.id}>
                    {agency.name}
                  </option>
                ))}
              </Form.Control>
              {reassignType !== 'agency' && (
                <small className="text-muted">
                  Seleziona "Assegna a Agenzia" per abilitare questa opzione
                </small>
              )}
              {reassignSelectedAgencyId && (
                <div className="mt-2">
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => handleReassign(null, reassignSelectedAgencyId)}
                  >
                    Assegna a {agencies.find(a => a.id == reassignSelectedAgencyId)?.name}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="mb-3">
            <Button 
              variant="outline-danger" 
              size="sm"
              onClick={() => handleReassign(null, null)}
            >
              Rimuovi assegnazione
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowReassignModal(false)
            setReassignType('')
            setReassignSelectedUserId('')
            setReassignSelectedAgencyId('')
          }}>
            Annulla
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Contact Status Quick-Change Confirmation Modal */}
      <Modal show={showContactStatusModal} onHide={handleCancelContactStatus} centered>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Cambio Stato Contatto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingContactStatus && (
            <div>
              <p>Sei sicuro di voler cambiare lo stato del contatto <strong>{inputs.name} {inputs.surname}</strong> in <strong>{pendingContactStatus}</strong>?</p>
              {pendingContactStatus === 'Richiamo Concordato' && (
                <div className="mt-3">
                  <Form.Label>Data per richiamare</Form.Label>
                  <Form.Control
                    type="date"
                    value={pendingDataPerRichiamare}
                    onChange={(e) => setPendingDataPerRichiamare(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelContactStatus} disabled={savingContactStatus}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleConfirmContactStatus} disabled={savingContactStatus}>
            {savingContactStatus ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Salvataggio...
              </>
            ) : (
              'Conferma'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* State Change Confirmation Modal */}
      <Modal show={showStateChangeModal} onHide={() => setShowStateChangeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Cambio Stato</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOpportunity && selectedNewState && (
            <div>
              <p>Sei sicuro di voler cambiare lo stato dell'opportunità <strong>{selectedOpportunity.name}</strong>?</p>
              <div className="mt-3">
                <p className="mb-2">
                  <strong>Stato attuale:</strong> 
                  <span className={`badge ms-2 ${
                    selectedOpportunity?.state?.state === '1' ? 'bg-primary' :
                    selectedOpportunity?.state?.state === '13' ? 'bg-warning' :
                    selectedOpportunity?.state?.state === '12' || selectedOpportunity?.state?.state === 'vinto' ? 'bg-success' :
                    selectedOpportunity?.state?.state === '10' || selectedOpportunity?.state?.state === 'archiviato' ? 'bg-secondary' :
                    'bg-info'
                  }`}>
                    {selectedOpportunity?.state?.name || 'Stato non disponibile'}
                  </span>
                </p>
                <p className="mb-0">
                  <strong>Nuovo stato:</strong> 
                  <span className={`badge ms-2 ${
                    selectedNewState?.state === '1' ? 'bg-primary' :
                    selectedNewState?.state === '13' ? 'bg-warning' :
                    selectedNewState?.state === '12' || selectedNewState?.state === 'vinto' ? 'bg-success' :
                    selectedNewState?.state === '10' || selectedNewState?.state === 'archiviato' ? 'bg-secondary' :
                    'bg-info'
                  }`}>
                    {selectedNewState?.name || 'Stato non disponibile'}
                  </span>
                </p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowStateChangeModal(false)
              setSelectedOpportunity(null)
              setSelectedNewState(null)
            }}
          >
            Annulla
          </Button>
          <Button 
            variant="primary" 
            onClick={handleConfirmStateChange}
          >
            Conferma Cambio
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Opportunity Without Quote Modal */}
      <Modal show={showCreateOpportunityWithoutQuoteModal} onHide={() => setShowCreateOpportunityWithoutQuoteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Crea Opportunità Senza Preventivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateOpportunityWithoutQuoteForm 
            products={products} 
            institutes={institutes}
            campaigns={campaigns} 
            onSubmit={handleCreateOpportunityWithoutQuote}
            onClose={() => setShowCreateOpportunityWithoutQuoteModal(false)}
          />
        </Modal.Body>
      </Modal>

      {/* Delete Trattenuta Confirmation Modal */}
      <Modal show={showDeleteTrattenutaModal} onHide={() => setShowDeleteTrattenutaModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Eliminazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {trattenutaToDelete && (
            <div>
              <p>Sei sicuro di voler eliminare questa trattenuta?</p>
              <div className="mt-3 p-3 bg-light rounded">
                <p className="mb-1"><strong>Tipo:</strong> {trattenutaToDelete.tipo}</p>
                <p className="mb-1"><strong>Rata:</strong> €{trattenutaToDelete.amount}</p>
                <p className="mb-0"><strong>Durata:</strong> {trattenutaToDelete.duration} mesi</p>
              </div>
              <p className="text-danger mt-3 mb-0">
                <small>Questa azione non può essere annullata.</small>
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteTrattenutaModal(false)}
            disabled={isDeletingTrattenuta}
          >
            Annulla
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteTrattenuta}
            disabled={isDeletingTrattenuta}
          >
            {isDeletingTrattenuta ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Eliminazione...
              </>
            ) : (
              'Elimina'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Go Back to Previous State Modal */}
      <Modal show={showGoBackModal} onHide={() => setShowGoBackModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Torna allo stato precedente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Seleziona lo stato a cui vuoi tornare per l'opportunità <strong>{opportunityToGoBack?.product?.name}</strong>.</p>
          <Form.Group>
            <Form.Label>Stato precedente</Form.Label>
            <Form.Control
              as="select"
              value={selectedPreviousState}
              onChange={(e) => setSelectedPreviousState(e.target.value)}
            >
              <option value="">Seleziona uno stato...</option>
              {Object.entries(opportunityStatesMap)
                .filter(([code]) => {
                  if (!opportunityToGoBack?.state?.state) return true;
                  const current = parseInt(opportunityToGoBack.state.state);
                  const target = parseInt(code);
                  return !isNaN(target) && !isNaN(current) && target <= current;
                })
                .map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGoBackModal(false)}>
            Annulla
          </Button>
          <Button 
            variant="primary" 
            onClick={handleConfirmGoBack}
            disabled={!selectedPreviousState}
          >
            Conferma
          </Button>
        </Modal.Footer>
      </Modal>

      {/* History Modal */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Cronologia Stati - {historyOpportunity?.product?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingHistory ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Caricamento...</span>
              </div>
            </div>
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Utente</th>
                  <th>Da Stato</th>
                  <th>A Stato</th>
                  {/* <th>Azioni</th> */}
                </tr>
              </thead>
              <tbody>
                {historyData.length > 0 ? (
                  historyData.map((item) => (
                    <tr key={item.id}>
                      <td>{new Date(item.timestamp).toLocaleString('it-IT')}</td>
                      <td>{item.user ? `${item.user.name} ${item.user.surname}` : '-'}</td>
                      <td>{item.from_state?.name || '-'}</td>
                      <td>{item.to_state?.name || '-'}</td>
                      {/* <td>
                        {item.from_state?.state && (
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => handleRevertState(item.from_state.state)}
                                title="Torna a questo stato"
                            >
                                Ripristina
                            </Button>
                        )}
                      </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">Nessuna cronologia disponibile</td>
                    {/* <td colSpan="5" className="text-center">Nessuna cronologia disponibile</td> */}
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

// Opportunity Form Component
const OpportunityForm = ({ onSubmit, onClose, products, institutes, campaigns, initialData }) => {
  const [formData, setFormData] = useState({
    name: '',
    product: '',
    institute: '',
    campaign: '',
    rata: '',
    durata: '',
    start_date: '',
    qualifica: '',
    convenzione: '',
    importo_rata: '',
    provvigione: '',
    data_decorrenza: '',
    rinovo: '',
    residuo: '',
    banca: '',
    assicurazione: '',
    capitale_lordo: '',
    netto_ricavo: '',
    TAN: '',
    TAEG: '',
    TEG: '',
    totale_assicurazione: '',
    importo_richiesto: '',
    preventivi: ''
  })

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        product: initialData.product?.id || initialData.product || '',
        institute: initialData.institute?.id || initialData.institute || '',
        campaign: initialData.campaign?.id || initialData.campaign || '',
      })
    }
  }, [initialData])

  const handleChange = (e) => {
    const field = e.target.name
    const value = e.target.value


    if (field === 'residuo') {
    if (value.includes('.') && value.split('.')[1].length > 1) {
        return
    }
    }

    // Enforce 0 decimal places
    if (field === 'rata') {
    if (value.includes('.')) {
        return
    }
    }

    setFormData({ ...formData, [field]: value })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!initialData) {
      onSubmit(formData)
    } else {
      onClose()
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Prodotto</Form.Label>
            <Form.Control as="select" name="product" value={formData.product || ''} onChange={handleChange} disabled={!!initialData}>
              <option value="">Seleziona prodotto</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>{product.name}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Istituto</Form.Label>
            <Form.Control as="select" name="institute" value={formData.institute || ''} onChange={handleChange} disabled={!!initialData}>
              <option value="">Seleziona istituto</option>
              {(institutes || []).map(inst => (
                <option key={inst.id} value={inst.id}>{inst.name}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Campagna</Form.Label>
            <Form.Control as="select" name="campaign" value={formData.campaign || ''} onChange={handleChange} disabled={!!initialData}>
              <option value="">Seleziona campagna</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Qualifica</Form.Label>
            <Form.Control type="text" name="qualifica" value={formData.qualifica || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>

        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Convenzione</Form.Label>
            <Form.Control type="text" name="convenzione" value={formData.convenzione || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Banca</Form.Label>
            <Form.Control type="text" name="banca" value={formData.banca || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Assicurazione</Form.Label>
            <Form.Control type="text" name="assicurazione" value={formData.assicurazione || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>

        <Col md="3">
          <Form.Group className="mb-3">
            <Form.Label>Rata (€)</Form.Label>
            <CurrencyInput step="0.1" name="rata" value={formData.rata || ''} onChange={handleChange} onWheel={(e) => e.target.blur()} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="3">
          <Form.Group className="mb-3">
            <Form.Label>Importo Rata (€)</Form.Label>
            <CurrencyInput step="0.1" name="importo_rata" value={formData.importo_rata || ''} onChange={handleChange} onWheel={(e) => e.target.blur()} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="3">
          <Form.Group className="mb-3">
            <Form.Label>Durata (mesi)</Form.Label>
            <Form.Control type="number" name="durata" value={formData.durata || ''} onChange={handleChange} onWheel={(e) => e.target.blur()} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="3">
          <Form.Group className="mb-3">
            <Form.Label>Provvigione (%)</Form.Label>
            <Form.Control type="number" step="0.01" name="provvigione" value={formData.provvigione || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>

        <Col md="3">
          <Form.Group className="mb-3">
            <Form.Label>Capitale Lordo (€)</Form.Label>
            <CurrencyInput step="0.01" name="capitale_lordo" value={formData.capitale_lordo || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="3">
          <Form.Group className="mb-3">
            <Form.Label>Netto Ricavo (€)</Form.Label>
            <CurrencyInput step="0.01" name="netto_ricavo" value={formData.netto_ricavo || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="3">
          <Form.Group className="mb-3">
            <Form.Label>Importo Richiesto (€)</Form.Label>
            <CurrencyInput step="0.01" name="importo_richiesto" value={formData.importo_richiesto || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="3">
          <Form.Group className="mb-3">
            <Form.Label>Totale Assicurazione (€)</Form.Label>
            <CurrencyInput step="0.01" name="totale_assicurazione" value={formData.totale_assicurazione || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>

        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>TAN (%)</Form.Label>
            <Form.Control type="number" step="0.01" name="TAN" value={formData.TAN || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>TAEG (%)</Form.Label>
            <Form.Control type="number" step="0.01" name="TAEG" value={formData.TAEG || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>TEG (%)</Form.Label>
            <Form.Control type="number" step="0.01" name="TEG" value={formData.TEG || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>

        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Data Decorrenza</Form.Label>
            <Form.Control type="date" name="data_decorrenza" value={formData.data_decorrenza || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Data Preventivo</Form.Label>
            <Form.Control type="date" name="start_date" value={formData.start_date || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Rinnovo</Form.Label>
            <Form.Control type="text" name="rinovo" value={formData.rinovo || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>

        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Residuo (€)</Form.Label>
            <CurrencyInput step="0.01" name="residuo" value={formData.residuo || ''} onChange={handleChange} readOnly={!!initialData} />
          </Form.Group>
        </Col>

      </Row>
      <div className="d-flex justify-content-end">
        <Button variant="secondary" className="me-2" onClick={onClose}>
          {initialData ? 'Chiudi' : 'Annulla'}
        </Button>
        {!initialData && (
          <Button variant="primary" type="submit">
            Crea Opportunità
          </Button>
        )}
      </div>
    </Form>
  )
}

// Trattenuta Form Component
const TrattenutaForm = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    tipo: '',
    amount: 0,
    duration: 0,
    start_date: '',
    residuo: 0
  })

  useEffect(() => {
    if (formData.tipo === 'Cessione del Quinto' || formData.tipo === 'Delega') {
      const residuo = residuoRateResidue({
        amount: formData.amount,
        duration: formData.duration,
        start_date: formData.start_date,
        residuo: 0
      })
      setFormData(prev => ({ ...prev, residuo }))
    }
  }, [formData.amount, formData.duration, formData.tipo, formData.start_date])

  const handleChange = (e) => {
    const field = e.target.name
    const value = e.target.value


    if (field === 'residuo') {
    if (value.includes('.') && value.split('.')[1].length > 1) {
        return
    }
    }

    // Enforce 0 decimal places
    if (field === 'amount') {
    if (value.includes('.')) {
        return
    }
    }
    setFormData({ ...formData, [field]: value })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Map fields to match backend expectations while preserving new fields
    const dataToSend = {
      ...formData,
      tipo: formData.tipo,
      amount: formData.amount,
      start_date: formData.start_date,
      duration: formData.duration,
    residuo: formData.residuo
    }
    
    onSubmit(dataToSend)
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md="12">
          <Form.Group className="mb-3">
            <Form.Label>Tipo <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="select"
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              required
            >
              <option value="">Seleziona</option>
              <option value="Cessione del Quinto">Cessione del Quinto</option>
              <option value="Delega">Delega</option>
              <option value="Pignoramento">Pignoramento</option>
              <option value="Altri Prestiti">Altri Prestiti</option>
            </Form.Control>
          </Form.Group>
        </Col>

        {(formData.tipo === 'Cessione del Quinto' || formData.tipo === 'Delega') && (
          <>
            <Col md="4">
              <Form.Group className="mb-3">
                <Form.Label>Rata (€) <span className="text-danger">*</span></Form.Label>
                <CurrencyInput 
                  name="amount" 
                  step="1"
                  value={formData.amount} 
                  onChange={handleChange} 
                  onWheel={(e) => e.target.blur()}
                  required 
                />
              </Form.Group>
            </Col>
            <Col md="4">
              <Form.Group className="mb-3">
                <Form.Label>Durata (mesi) <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="select"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleziona</option>
                  <option value="24">24</option>
                  <option value="36">36</option>
                  <option value="48">48</option>
                  <option value="60">60</option>
                  <option value="72">72</option>
                  <option value="84">84</option>
                  <option value="96">96</option>
                  <option value="108">108</option>
                  <option value="120">120</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md="4">
              <Form.Group className="mb-3">
                <Form.Label>Decorrenza <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                  type="date" 
                  name="start_date" 
                  value={formData.start_date} 
                  onChange={handleChange} 
                  required 
                />
              </Form.Group>
            </Col>
          </>
        )}

        {formData.tipo === 'Altri Prestiti' && (
          <Col md="12">
            <Form.Group className="mb-3">
              <Form.Label>Residuo (€) <span className="text-danger">*</span></Form.Label>
              <CurrencyInput 
                name="residuo"
                step="0.1"
                value={formData.residuo} 
                onChange={handleChange}
                onWheel={(e) => e.target.blur()}
                required 
              />
            </Form.Group>
          </Col>
        )}
      </Row>
      <div className="d-flex justify-content-end">
        <Button variant="secondary" className="me-2" onClick={onClose}>
          Annulla
        </Button>
        <Button variant="primary" type="submit">
          Crea Trattenuta
        </Button>
      </div>
    </Form>
  )
}

const CreateOpportunityWithoutQuoteForm = ({ products, institutes, campaigns, onSubmit, onClose }) => {
  const [product, setProduct] = useState('')
  const [institute, setInstitute] = useState('')
  const [campaign, setCampaign] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({ product, institute, campaign })
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label>Prodotto <span className="text-danger">*</span></Form.Label>
        <Form.Control 
          as="select" 
          value={product} 
          onChange={(e) => setProduct(e.target.value)} 
          required
        >
          <option value="">Seleziona prodotto</option>
          {products.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </Form.Control>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Istituto</Form.Label>
        <Form.Control 
          as="select" 
          value={institute} 
          onChange={(e) => setInstitute(e.target.value)}
        >
          <option value="">Seleziona istituto</option>
          {(institutes || []).map(inst => (
            <option key={inst.id} value={inst.id}>{inst.name}</option>
          ))}
        </Form.Control>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Campagna</Form.Label>
        <Form.Control 
          as="select" 
          value={campaign} 
          onChange={(e) => setCampaign(e.target.value)}
        >
          <option value="">Seleziona campagna</option>
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Form.Control>
      </Form.Group>

      <div className="d-flex justify-content-end">
        <Button variant="secondary" className="me-2" onClick={onClose}>Annulla</Button>
        <Button variant="primary" type="submit" disabled={!product}>Crea</Button>
      </div>
    </Form>
  )
}

export default ContactAddOrView


